import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FormControl, Radio, RadioGroup, useTheme } from "@mui/material";
import { Careprovider, MUIPickersDate } from "@recare/core/types";
import {
  FormElement,
  FormElementProps,
  FormWatcher,
} from "@recare/react-forms-state";
import { useTranslations } from "@recare/translations";
import { addDays, endOfDay, getUnixTime, startOfDay } from "date-fns";
import DatePickerInputField from "ds/components/DatePickerInputField";
import { SubheadingFormLabel } from "ds/components/FormComponents/SubheadingFormLabel";
import TimePicker from "ds/components/TimePicker";
import { HorizontalLayout, VerticalLayout } from "ds/materials/layouts";
import { border, dp, margin, padding, sizing } from "ds/materials/metrics";
import { getFocusVisibleOutline } from "ds/materials/theme";
import {
  Body,
  FONT_SIZE_13,
  FONT_SIZE_18,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_SEMI_BOLD,
} from "ds/materials/typography";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { useClientRect } from "dsl/hooks";
import { AsteriskExplained } from "dsl/molecules/AsteriskExplained";
import { kebabCase } from "lodash";
import styled from "styled-components";
import { shouldHandleTime } from ".";

type FormValue = {
  _capacity_time: MUIPickersDate;
  available_date: MUIPickersDate;
  has_capacity: boolean;
};

type CapacitySelectorProps = {
  careprovider: Careprovider;
};

type CapacityRadioGroupInputProps = {
  hasCapacity: boolean;
  labelId: string;
  onChange: (value: boolean) => void;
  tileRef: React.MutableRefObject<HTMLDivElement | null>;
};

type CapacityTileProps = {
  ariaExpanded?: boolean;
  isSelected: boolean;
  subtitle?: string;
  tileRef?: React.MutableRefObject<HTMLDivElement | null>;
  title: string;
  value: boolean;
};

type TileRadioElementProps = {
  ariaExpanded?: boolean;
  id: string;
  isSelected: boolean;
  subtitle?: string;
  title: string;
  value: boolean;
};

const ELEMENT_NAME_CAPACITY = "has_capacity";
const TODAY_MIDNIGHT = getUnixTime(startOfDay(addDays(new Date(), 1)));
const TODAY_60DAYS = getUnixTime(endOfDay(addDays(new Date(), 60)));

const TileWrapper = styled.div<{
  isSelected: boolean;
}>`
  outline: ${({ isSelected, theme }) =>
    isSelected
      ? border({ width: 2, color: theme.palette.primary.main })
      : border({ color: theme.palette.grey[600] })};
  border-radius: ${sizing(1)};
  background-color: ${({ isSelected, theme }) =>
    isSelected ? theme.palette.primary.light : undefined};
  min-width: ${sizing(36)};
  min-height: ${sizing(20)};
  position: relative;
  flex: 1;

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.light};
    cursor: pointer;
  }
`;

const RadioWrapper = styled.div`
  height: 100%;
  width: 100%;
  padding: ${padding(3)};
  box-sizing: border-box;
`;

function DatePickerElement({
  labelId,
  showTimePicker,
  tileWidth,
  value,
}: {
  labelId: string;
  showTimePicker: boolean | undefined;
  tileWidth: number | undefined;
  value: FormValue;
}) {
  const theme = useTheme();
  const { isMobile } = useMedia();
  const translations = useTranslations();

  return (
    <VerticalLayout
      style={{
        width: isMobile ? "100%" : tileWidth,
        alignContent: "flex-start",
      }}
    >
      <ExpandMoreIcon
        style={{ alignSelf: "center", margin: margin(0, 0, 1.5) }}
      />
      <SubheadingFormLabel
        aria-labelledby={labelId}
        sx={{
          margin: margin(0, 0, 1),
          fontSize: FONT_SIZE_13,
          fontWeight: FONT_WEIGHT_SEMI_BOLD,
        }}
      >
        {
          translations.careproviderApp.settings.capacity.tileNoCapacity
            .dateInformation
        }
      </SubheadingFormLabel>
      <AsteriskExplained
        style={{ color: theme.palette.grey[600] }}
        customMargin={margin(0)}
      />
      <HorizontalLayout
        padding={padding(2, 0, 0)}
        gap={sizing(1)}
        flexWrap="wrap"
      >
        <DatePickerInputField
          blockBefore={showTimePicker ? undefined : TODAY_MIDNIGHT}
          blockAfter={TODAY_60DAYS}
          disablePast
          elementName="available_date"
          inputSx={{
            display: "flex",
            flex: 1,
            "& .MuiInputBase-root": {
              backgroundColor: theme.palette.common.white,
            },
          }}
          label={
            translations.careproviderProfile.capacity.declineExpirationDate
          }
          required
          textInputVariant="outlined"
          hasCustomValidation
          value={value.available_date}
        />
        {showTimePicker && (
          <TimePicker
            elementName="_capacity_time"
            inputSx={{
              display: "flex",
              flex: 1,
              "& .MuiInputBase-root": {
                backgroundColor: theme.palette.common.white,
              },
            }}
            label={translations.auctionRequest.capacityTime}
            required
            textInputVariant="outlined"
            wrapperStyle={{ display: "flex", flex: 1 }}
            hasCustomValidation
            timeValue={value._capacity_time}
          />
        )}
      </HorizontalLayout>
    </VerticalLayout>
  );
}

const TileRadioElement = ({
  ariaExpanded,
  id,
  isSelected,
  subtitle,
  title,
  value,
}: TileRadioElementProps) => {
  const theme = useTheme();
  return (
    <Radio
      checked={isSelected}
      checkedIcon={
        <RadioWrapper>
          <Body
            as="h2"
            fontSize={FONT_SIZE_18}
            fontWeight={FONT_WEIGHT_BOLD}
            margin={margin(0, 0, 2)}
          >
            {title}
          </Body>
          <Body as="p" color={theme.palette.grey[600]} margin={margin(0)}>
            {subtitle}
          </Body>
        </RadioWrapper>
      }
      disableTouchRipple
      focusRipple
      icon={
        <RadioWrapper>
          <Body
            as="h2"
            fontSize={FONT_SIZE_18}
            fontWeight={FONT_WEIGHT_BOLD}
            margin={margin(0, 0, 2)}
          >
            {title}
          </Body>
          <Body as="p" color={theme.palette.grey[600]} margin={margin(0)}>
            {subtitle}
          </Body>
        </RadioWrapper>
      }
      id={id}
      inputProps={{
        "aria-expanded": ariaExpanded,
      }}
      name="has_capacity"
      sx={{
        position: "absolute",
        width: "100%",
        height: "100%",
        margin: "0",
        padding: "0",
        ...getFocusVisibleOutline(),
        borderRadius: 0,
        justifyContent: "flex-start",
        outlineOffset: dp(4),

        "&:hover": { backgroundColor: "transparent" },
      }}
      value={value}
      required
    />
  );
};

function getId(elementName: string, name: string) {
  return `tile-${elementName}-${name
    .replace(/[ :]/g, "")
    .replace("<", "below")}`;
}

const CapacityTile = ({
  ariaExpanded,
  isSelected,
  subtitle,
  tileRef,
  title,
  value,
}: CapacityTileProps) => {
  const id = getId("has_capacity", title);
  return (
    <TileWrapper isSelected={isSelected} ref={tileRef}>
      <label key={`tile-${title}`} htmlFor={id}>
        <TileRadioElement
          ariaExpanded={ariaExpanded}
          id={id}
          isSelected={isSelected}
          title={title}
          subtitle={subtitle}
          value={value}
        />
      </label>
    </TileWrapper>
  );
};

function CapacityRadioGroup({
  hasCapacity,
  labelId,
  onChange,
  tileRef,
}: CapacityRadioGroupInputProps) {
  const translations = useTranslations();
  return (
    <RadioGroup
      aria-labelledby={labelId}
      id={ELEMENT_NAME_CAPACITY}
      onChange={(event) => {
        onChange(JSON.parse(event.target.value));
      }}
      row
      sx={{
        margin: margin(2, 0),
        gap: sizing(2),
        display: "flex",
      }}
    >
      {/* Has capacity tile */}
      <CapacityTile
        isSelected={hasCapacity}
        title={
          translations.careproviderApp.settings.capacity.tileCapacity.title
        }
        subtitle={
          translations.careproviderApp.settings.capacity.tileCapacity.text
        }
        value={true}
      />

      {/* Has no capacity tile */}
      <CapacityTile
        ariaExpanded={!hasCapacity}
        isSelected={!hasCapacity}
        title={
          translations.careproviderApp.settings.capacity.tileNoCapacity.title
        }
        tileRef={tileRef}
        subtitle={
          translations.careproviderApp.settings.capacity.tileNoCapacity.text
        }
        value={false}
      />
    </RadioGroup>
  );
}

const CapacityRadioGroupInputField = FormElement()<
  FormElementProps & CapacityRadioGroupInputProps
>(CapacityRadioGroup);

export function CapacitySelector({ careprovider }: CapacitySelectorProps) {
  const translations = useTranslations();
  const [rect, tileRef] = useClientRect<HTMLDivElement>();

  const label = translations.careproviderApp.settings.capacity.instructions;
  const labelId = kebabCase(label);
  const showTimePicker = shouldHandleTime({
    searchType: careprovider?.patient_type,
  });

  return (
    <FormControl component="fieldset" sx={{ width: "100%" }}>
      <SubheadingFormLabel
        id={labelId}
        sx={{ fontSize: FONT_SIZE_13, fontWeight: FONT_WEIGHT_BOLD }}
      >
        {label}
      </SubheadingFormLabel>
      <FormWatcher watchPath={ELEMENT_NAME_CAPACITY}>
        {({ value, watchedValue }) => {
          const hasCapacity = watchedValue === false ? false : true;
          return (
            <VerticalLayout overflow="visible">
              <CapacityRadioGroupInputField
                elementName={ELEMENT_NAME_CAPACITY}
                hasCapacity={hasCapacity}
                labelId={labelId}
                tileRef={tileRef}
              />
              {!hasCapacity && (
                <div
                  hidden
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    margin: margin(0, 0, 1),
                  }}
                >
                  <DatePickerElement
                    labelId={labelId}
                    showTimePicker={showTimePicker}
                    tileWidth={rect?.width}
                    value={value}
                  />
                </div>
              )}
            </VerticalLayout>
          );
        }}
      </FormWatcher>
    </FormControl>
  );
}
